import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box, Button, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Chip, IconButton, Dialog, TextField, Select, MenuItem,
  Switch, Tooltip, CircularProgress, Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/Print";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CloseIcon from "@mui/icons-material/Close";
import JsBarcode from "jsbarcode";
import {
  useGetProductBarcodesQuery,
  useAddProductBarcodeMutation,
  useSetBarcodePrimaryMutation,
  useDeleteBarcodeMutation,
} from "../../Api/attoDeskApi";

interface Props {
  productId: string;
  productName?: string;
  price?: number;
  autoOpenPrint?: boolean;
  onAutoOpenHandled?: () => void;
}

const TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  SYSTEM: { bg: "#DBEAFE", color: "#2563EB" },
  EAN13: { bg: "#D1FAE5", color: "#059669" },
  CODE128: { bg: "#EDE9FE", color: "#7C3AED" },
  QR: { bg: "#FEF3C7", color: "#D97706" },
};

const BarcodeManager: React.FC<Props> = ({ productId, productName, price, autoOpenPrint, onAutoOpenHandled }) => {
  const { data, isLoading, refetch } = useGetProductBarcodesQuery(productId);
  const [addBarcode] = useAddProductBarcodeMutation();
  const [setPrimary] = useSetBarcodePrimaryMutation();
  const [deleteBarcode] = useDeleteBarcodeMutation();

  const [addOpen, setAddOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [form, setForm] = useState({ barcode: "", type: "CODE128", is_primary: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Print settings
  const [labelSize, setLabelSize] = useState<"2x1" | "3x2">("2x1");
  const [showName, setShowName] = useState(true);
  const [showPrice, setShowPrice] = useState(true);
  const [showNumber, setShowNumber] = useState(true);
  const [printQty, setPrintQty] = useState(1);
  const [selectedPrintBarcode, setSelectedPrintBarcode] = useState<string>("");
  const previewRef = useRef<SVGSVGElement | null>(null);
  const [barcodeReady, setBarcodeReady] = useState(false);

  const barcodes = (data as any)?.data || [];

  // Auto-open print dialog when triggered externally
  useEffect(() => {
    if (autoOpenPrint && barcodes.length > 0) {
      setSelectedPrintBarcode(barcodes.find((b: any) => b.is_primary)?.barcode || barcodes[0]?.barcode || "");
      setPrintOpen(true);
      onAutoOpenHandled?.();
    }
  }, [autoOpenPrint, barcodes]);

  // Render the preview SVG only when its inputs actually change.
  // Crucially, this does NOT depend on printQty — quantity changes must not
  // regenerate the preview barcode (was the cause of the freeze).
  // Uses requestAnimationFrame so the SVG is fully mounted in the dialog
  // portal before JsBarcode tries to write into it (otherwise the first
  // open of the modal renders a blank preview).
  useEffect(() => {
    setBarcodeReady(false);
    if (!printOpen) return;
    if (!selectedPrintBarcode) return;
    let cancelled = false;
    const handle = requestAnimationFrame(() => {
      if (cancelled) return;
      const svg = previewRef.current;
      if (!svg) return;
      // Render number as a separate <div> (the spec layout), so the SVG is
      // pure barcode and the number text can be styled independently.
      const opts = labelSize === "2x1"
        ? { width: 1.6, height: 50, margin: 4 }
        : { width: 2.3, height: 70, margin: 6 };
      try {
        JsBarcode(svg, selectedPrintBarcode, {
          format: "CODE128",
          width: opts.width,
          height: opts.height,
          margin: opts.margin,
          displayValue: false,
          background: "#ffffff",
          lineColor: "#000000",
        });
        setBarcodeReady(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Barcode render failed", err);
      }
    });
    return () => { cancelled = true; cancelAnimationFrame(handle); };
  }, [printOpen, selectedPrintBarcode, labelSize]);

  const handleAdd = async () => {
    if (!form.barcode.trim()) { setError("Barcode is required"); return; }
    setSaving(true); setError("");
    try {
      const res = await addBarcode({ productId, barcode: form.barcode.trim(), type: form.type, is_primary: form.is_primary }).unwrap();
      if ((res as any).success !== false) {
        setAddOpen(false);
        setForm({ barcode: "", type: "CODE128", is_primary: false });
        refetch();
      } else { setError((res as any).message || "Failed"); }
    } catch (e: any) { setError(e.data?.message || "Barcode already exists or invalid"); }
    finally { setSaving(false); }
  };

  // Thermal-safe barcode render settings — width/height in px, margin = quiet zone
  const barcodeOpts = labelSize === "2x1"
    ? { width: 1.8, height: 55, margin: 10, fontSize: 12 }
    : { width: 2.5, height: 80, margin: 14, fontSize: 16 };

  const handlePrint = () => {
    const bc = selectedPrintBarcode || barcodes.find((b: any) => b.is_primary)?.barcode || barcodes[0]?.barcode;
    if (!bc) return;

    // Label canvas — 2.25"×1" or 4"×2" at 96 dpi (thermal-friendly aspect)
    const w = labelSize === "2x1" ? 216 : 384;
    const h = labelSize === "2x1" ? 96 : 192;

    const labels: string[] = [];
    for (let i = 0; i < printQty; i++) {
      labels.push(`
        <div class="barcode-label">
          ${showName ? `<div class="barcode-product-name">${productName || ""}</div>` : ""}
          <svg id="bc-${i}" class="barcode-svg"></svg>
          ${showPrice ? `<div class="barcode-price">$${Number(price || 0).toFixed(2)}</div>` : ""}
        </div>
      `);
    }

    const pageSize = labelSize === "2x1" ? "2.25in 1in" : "4in 2in";
    const labelW = labelSize === "2x1" ? "2.25in" : "4in";
    const labelH = labelSize === "2x1" ? "1in"    : "2in";
    const titleSize = labelSize === "2x1" ? 11 : 14;
    const priceSize = labelSize === "2x1" ? 13 : 17;
    const svgHeight = labelSize === "2x1" ? 42 : 70;
    const win = window.open("", "_blank", "width=900,height=600");
    if (!win) { alert("Popup blocked — please allow popups to print labels."); return; }
    win.document.write(`<!DOCTYPE html><html><head><title>Print Labels — ${productName || ""}</title>
      <style>
        body { margin: 0; font-family: Arial, Helvetica, sans-serif; background: #f3f4f6; }
        #label-print-area { display: flex; flex-wrap: wrap; gap: 4px; padding: 12px; background: #fff; }

        /* Screen preview of the print page */
        .barcode-label {
          width: ${labelW};
          height: ${labelH};
          padding: 8px 6px 6px;
          box-sizing: border-box;
          background: #fff;
          color: #000;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          page-break-inside: avoid;
          overflow: hidden;
        }
        .barcode-product-name {
          font-size: ${titleSize}px;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 4px;
          text-align: center;
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .barcode-svg {
          display: block;
          max-width: 100%;
          height: ${svgHeight}px;
        }
        .barcode-price {
          margin-top: 4px;
          font-size: ${priceSize}px;
          font-weight: 700;
        }

        @page { size: ${pageSize}; margin: 0; }

        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }
          body * { visibility: hidden !important; }
          #label-print-area, #label-print-area * { visibility: visible !important; }
          #label-print-area {
            position: absolute;
            left: 0;
            top: 0;
            padding: 0;
            gap: 0;
            background: #fff;
          }
          .barcode-label {
            /* Top padding pulls the title away from the page edge so the
               browser print engine doesn't clip "Apple Gala" at the top. */
            padding: 10px 8px 6px !important;
            justify-content: flex-start !important;
            overflow: hidden;
            border: none !important;
          }
          .barcode-product-name {
            font-size: 12px !important;
            font-weight: 700 !important;
            line-height: 1.2 !important;
            margin-bottom: 4px !important;
          }
          .barcode-svg {
            max-width: 100% !important;
            height: ${svgHeight}px !important;
          }
          .barcode-price {
            margin-top: 4px !important;
            font-size: 14px !important;
            font-weight: 700 !important;
          }
        }
      </style>
    </head><body><div id="label-print-area">${labels.join("")}</div></body></html>`);
    win.document.close();

    const printOpts = labelSize === "2x1"
      ? { width: 1.6, height: svgHeight, margin: 6,  fontSize: 11 }
      : { width: 2.3, height: svgHeight, margin: 10, fontSize: 14 };
    const renderAll = () => {
      for (let i = 0; i < printQty; i++) {
        const el = win.document.getElementById(`bc-${i}`);
        if (el && !el.querySelector("rect, path")) {
          try {
            JsBarcode(el, bc, {
              format: "CODE128",
              width: printOpts.width,
              height: printOpts.height,
              margin: printOpts.margin,
              displayValue: showNumber,
              fontSize: printOpts.fontSize,
              background: "#ffffff",
              lineColor: "#000000",
            });
          } catch {}
        }
      }
    };

    win.onload = () => {
      renderAll();
      setTimeout(() => { win.focus(); win.print(); }, 250);
      win.onafterprint = () => win.close();
    };
    setTimeout(() => {
      renderAll();
      setTimeout(() => { try { win.focus(); win.print(); } catch {} }, 300);
    }, 500);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <QrCodeIcon sx={{ fontSize: 18, color: "#6B7280" }} />
          <Typography fontSize={14} fontWeight={600} color="#111827">Manage Barcodes</Typography>
          <Chip label={barcodes.length} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#F3F4F6", color: "#6B7280" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
            onClick={() => { setSelectedPrintBarcode(barcodes.find((b: any) => b.is_primary)?.barcode || ""); setPrintOpen(true); }}
            disabled={barcodes.length === 0}
            sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#374151" }}>
            Print Labels
          </Button>
          <Button size="small" variant="contained" disableElevation startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => { setForm({ barcode: "", type: "CODE128", is_primary: false }); setError(""); setAddOpen(true); }}
            sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: "#111827", "&:hover": { bgcolor: "#1F2937" } }}>
            Add Barcode
          </Button>
        </Box>
      </Box>

      {/* Table */}
      {isLoading ? (
        <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={20} /></Box>
      ) : barcodes.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4, border: "1px solid #E5E7EB", borderRadius: 2 }}>
          <QrCodeIcon sx={{ fontSize: 32, color: "#D1D5DB", mb: 1 }} />
          <Typography fontSize={13} color="#9CA3AF">No barcodes yet — system barcode will be created automatically</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ border: "1px solid #E5E7EB", borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                <TableCell sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280" }}>Barcode</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280" }}>Scan</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280" }} align="center">Type</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280" }} align="center">Primary</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 600, color: "#6B7280" }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {barcodes.map((bc: any) => {
                const tc = TYPE_COLORS[bc.type] || TYPE_COLORS.SYSTEM;
                return (
                  <TableRow key={bc.id} sx={{ "&:hover": { bgcolor: "#F9FAFB" } }}>
                    <TableCell>
                      <Typography fontSize={13} fontWeight={600} color="#111827" sx={{ fontFamily: "monospace" }}>{bc.barcode}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box ref={(el: HTMLDivElement | null) => {
                        if (el && !el.querySelector("svg")) {
                          const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
                          el.appendChild(svg);
                          try { JsBarcode(svg, bc.barcode, { width: 1, height: 28, displayValue: false, margin: 0, format: "CODE128" }); } catch {}
                        }
                      }} sx={{ minWidth: 80 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={bc.type} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: tc.bg, color: tc.color }} />
                    </TableCell>
                    <TableCell align="center">
                      {bc.is_primary ? <StarIcon sx={{ fontSize: 18, color: "#F59E0B" }} /> : <StarBorderIcon sx={{ fontSize: 18, color: "#D1D5DB" }} />}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                        {!bc.is_primary && (
                          <Tooltip title="Set as primary">
                            <IconButton size="small" onClick={async () => { await setPrimary(String(bc.id)).unwrap(); refetch(); }}
                              sx={{ width: 28, height: 28, border: "1px solid #E5E7EB", borderRadius: 1.5 }}>
                              <StarBorderIcon sx={{ fontSize: 14, color: "#F59E0B" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Print this barcode">
                          <IconButton size="small" onClick={() => { setSelectedPrintBarcode(bc.barcode); setPrintOpen(true); }}
                            sx={{ width: 28, height: 28, border: "1px solid #E5E7EB", borderRadius: 1.5 }}>
                            <PrintIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                          </IconButton>
                        </Tooltip>
                        {bc.type !== "SYSTEM" && (
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={async () => { await deleteBarcode(String(bc.id)).unwrap(); refetch(); }}
                              sx={{ width: 28, height: 28, border: "1px solid #E5E7EB", borderRadius: 1.5 }}>
                              <DeleteOutlineIcon sx={{ fontSize: 14, color: "#EF4444" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ADD BARCODE MODAL */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between" }}>
          <Typography fontSize={15} fontWeight={600} color="#111827">Add Barcode</Typography>
          <IconButton size="small" onClick={() => setAddOpen(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          {error && <Typography fontSize={12} color="#DC2626" fontWeight={600}>{error}</Typography>}
          <Box>
            <Typography fontSize={12} fontWeight={600} color="#374151" mb={0.75}>Barcode *</Typography>
            <TextField fullWidth size="small" placeholder="Enter or scan barcode" value={form.barcode}
              onChange={e => setForm(f => ({ ...f, barcode: e.target.value }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontFamily: "monospace", "& fieldset": { borderColor: "#E5E7EB" } } }} />
          </Box>
          <Box>
            <Typography fontSize={12} fontWeight={600} color="#374151" mb={0.75}>Type</Typography>
            <Select fullWidth size="small" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              sx={{ borderRadius: 1.5, "& fieldset": { borderColor: "#E5E7EB" } }}>
              <MenuItem value="EAN13">EAN-13</MenuItem>
              <MenuItem value="CODE128">Code 128</MenuItem>
              <MenuItem value="QR">QR Code</MenuItem>
            </Select>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch size="small" checked={form.is_primary} onChange={(_, v) => setForm(f => ({ ...f, is_primary: v }))} />
            <Typography fontSize={13} color="#374151" fontWeight={500}>Set as primary</Typography>
          </Box>
        </Box>
        <Box sx={{ px: 3, py: 2, bgcolor: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button variant="outlined" size="small" onClick={() => setAddOpen(false)}
            sx={{ borderRadius: 1.5, textTransform: "none", borderColor: "#E5E7EB", color: "#374151" }}>Cancel</Button>
          <Button variant="contained" disableElevation size="small" onClick={handleAdd} disabled={saving || !form.barcode.trim()}
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, bgcolor: "#111827", "&:hover": { bgcolor: "#1F2937" } }}>
            {saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Add Barcode"}
          </Button>
        </Box>
      </Dialog>

      {/* PRINT LABELS MODAL */}
      <Dialog open={printOpen} onClose={() => setPrintOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between" }}>
          <Typography fontSize={15} fontWeight={600} color="#111827">Print Labels</Typography>
          <IconButton size="small" onClick={() => setPrintOpen(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography fontSize={12} fontWeight={600} color="#374151" mb={0.75}>Barcode</Typography>
              <Select fullWidth size="small" value={selectedPrintBarcode} onChange={e => setSelectedPrintBarcode(e.target.value)}
                sx={{ borderRadius: 1.5, fontFamily: "monospace", "& fieldset": { borderColor: "#E5E7EB" } }}>
                {barcodes.map((b: any) => (
                  <MenuItem key={b.id} value={b.barcode} sx={{ fontFamily: "monospace" }}>
                    {b.barcode} {b.is_primary ? "★" : ""}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={3}>
              <Typography fontSize={12} fontWeight={600} color="#374151" mb={0.75}>Label Size</Typography>
              <Select fullWidth size="small" value={labelSize} onChange={e => setLabelSize(e.target.value as any)}
                sx={{ borderRadius: 1.5, "& fieldset": { borderColor: "#E5E7EB" } }}>
                <MenuItem value="2x1">2×1 inch</MenuItem>
                <MenuItem value="3x2">3×2 inch</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={3}>
              <Typography fontSize={12} fontWeight={600} color="#374151" mb={0.75}>Quantity</Typography>
              <TextField fullWidth size="small" type="number" value={printQty} onChange={e => setPrintQty(Math.max(1, Number(e.target.value)))}
                inputProps={{ min: 1, max: 100 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, "& fieldset": { borderColor: "#E5E7EB" } } }} />
            </Grid>
            <Grid item xs={12}>
              <Typography fontSize={12} fontWeight={600} color="#374151" mb={1}>Include on label:</Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                {[
                  { label: "Product Name", value: showName, setter: setShowName },
                  { label: "Price", value: showPrice, setter: setShowPrice },
                  { label: "Barcode Number", value: showNumber, setter: setShowNumber },
                ].map(opt => (
                  <Box key={opt.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Switch size="small" checked={opt.value} onChange={(_, v) => opt.setter(v)} />
                    <Typography fontSize={12} color="#374151">{opt.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Preview — sized to mimic printed label. SVG is always mounted
              (so the ref binds reliably) but hidden via opacity until
              JsBarcode fills it on the next animation frame. This prevents
              the blank-preview flash on first dialog open. */}
          <Box sx={{ mt: 2, p: 3, border: "1px solid #E5E7EB", borderRadius: 2, bgcolor: "#F9FAFB", textAlign: "center" }}>
            <Typography fontSize={10} color="#9CA3AF" mb={1.5}>PREVIEW</Typography>
            <Box
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 0.5,
                width: labelSize === "2x1" ? 240 : 400,
                minHeight: labelSize === "2x1" ? 130 : 220,
                p: "12px 10px 10px",
                border: "1px dashed #D1D5DB",
                borderRadius: 1,
                bgcolor: "#fff",
                boxSizing: "border-box",
                overflow: "visible",
                textAlign: "center",
              }}
            >
              {showName && (
                <Typography
                  sx={{
                    fontSize: labelSize === "2x1" ? 13 : 16,
                    fontWeight: 700,
                    color: "#000",
                    lineHeight: 1.2,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {productName}
                </Typography>
              )}
              {selectedPrintBarcode ? (
                <>
                  <svg
                    ref={previewRef}
                    style={{
                      display: "block",
                      width: labelSize === "2x1" ? 180 : 320,
                      maxWidth: "100%",
                      height: "auto",
                      opacity: barcodeReady ? 1 : 0,
                      transition: "opacity 80ms linear",
                    }}
                  />
                  {barcodeReady && showNumber && (
                    <Typography
                      sx={{
                        fontSize: labelSize === "2x1" ? 10 : 12,
                        color: "#666",
                        letterSpacing: 1,
                        fontFamily: "monospace",
                        lineHeight: 1.2,
                      }}
                    >
                      {selectedPrintBarcode}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography fontSize={11} color="#9CA3AF">No barcode available</Typography>
              )}
              {showPrice && (
                <Typography
                  sx={{
                    fontSize: labelSize === "2x1" ? 18 : 22,
                    fontWeight: 800,
                    color: "#000",
                    mt: 0.5,
                  }}
                >
                  ${Number(price || 0).toFixed(2)}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ px: 3, py: 2, bgcolor: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" disableElevation startIcon={<PrintIcon sx={{ fontSize: 16 }} />} onClick={handlePrint}
            disabled={!selectedPrintBarcode}
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, bgcolor: "#111827", "&:hover": { bgcolor: "#1F2937" } }}>
            Print {printQty} Label{printQty > 1 ? "s" : ""}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default BarcodeManager;
